import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { CarrierInsuranceStatusChip } from "components/CarrierInsuranceStatusChip";
import { NewTabLink } from "components/NewTabLink";
import { ISOStringToMonthDayYear } from "utils";

import { formatCargoCoverage } from "./utils";

import type { CompanyProfile } from "types/company";

const CARGO_COVERAGE_TOOLTIP =
  "Cargo coverage limit stated on this carrier's insurance certificate";

interface CargoCoverageProps {
  label: string;
  coverage: string;
}

function CargoCoverage(props: CargoCoverageProps) {
  const { label, coverage } = props;

  return (
    <Tooltip title={CARGO_COVERAGE_TOOLTIP}>
      <Typography variant="body2" color="text.secondary">
        {label}: {coverage}
      </Typography>
    </Tooltip>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow(props: DetailRowProps) {
  const { label, value } = props;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}

interface Props {
  companyProfile: CompanyProfile;
  chipSize?: "small" | "medium";
  shortVersion?: boolean;
}

export function CarrierInsuranceDocs(props: Props) {
  const { companyProfile, chipSize, shortVersion = false } = props;

  if (companyProfile.carrier_insurance) {
    const cargoCoverage = formatCargoCoverage({
      amountDollars:
        companyProfile.carrier_insurance.cargo_insurance_amount_dollars,
      currencyCode: companyProfile.carrier_insurance.cargo_insurance_currency,
    });

    if (shortVersion) {
      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          // The coverage is a third item on a row that sits inside another
          // fixed-width row, so it has to be allowed to wrap on small screens.
          // `spacing` is child margins unless `useFlexGap` is set, and those
          // margins survive the wrap and indent the wrapped item.
          flexWrap="wrap"
          useFlexGap
        >
          <NewTabLink href={companyProfile.carrier_insurance.url}>
            <Typography variant="body1">View Documents</Typography>
          </NewTabLink>
          <CarrierInsuranceStatusChip
            insurance={companyProfile.carrier_insurance}
            chipSize={chipSize}
          />
          <CargoCoverage label="Cargo" coverage={cargoCoverage} />
        </Stack>
      );
    }

    // Fills its column, so it ends level with the About card beside it.
    return (
      <Card sx={{ flex: 1 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Insurance Status</Typography>
              <CarrierInsuranceStatusChip
                insurance={companyProfile.carrier_insurance}
                chipSize={chipSize}
              />
            </Stack>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Last Updated:{" "}
              {ISOStringToMonthDayYear(
                companyProfile.carrier_insurance.created_at,
              )}
            </Typography>
          }
          action={
            <NewTabLink href={companyProfile.carrier_insurance.url}>
              <Typography variant="body1">View Documents</Typography>
            </NewTabLink>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={1} divider={<Divider flexItem />}>
            <Tooltip title={CARGO_COVERAGE_TOOLTIP}>
              <Box>
                <DetailRow label="Cargo Insurance" value={cargoCoverage} />
              </Box>
            </Tooltip>
            <DetailRow
              label="Expires"
              value={ISOStringToMonthDayYear(
                companyProfile.carrier_insurance.expires_on,
              )}
            />
          </Stack>
        </CardContent>
      </Card>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      This carrier has not added their documents yet.
    </Typography>
  );
}
